/**
 * Page /admin/contenu — éditeur visuel du contenu du site public.
 *
 * Inspiré de `AdminContent.tsx` de Sakinea : on définit des SECTIONS, chacune
 * ayant des CHAMPS (texte, longtexte, image). Les valeurs sont stockées en
 * key-value dans MongoDB (`site_content`) et chargées côté public via le hook
 * useSiteContent().
 *
 * Pour ajouter une nouvelle section éditable :
 *  1. ajoute son entrée dans CMS_SECTIONS ci-dessous
 *  2. dans la page React publique, charge la key via useSiteContent et utilise
 *     c("key.name", fallbackHardcoded)
 */
import { useEffect, useMemo, useState } from "react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, Save, RefreshCw, FileText, ChevronRight, Eye, X,
    Home as HomeIcon, BarChart3, Users, Building, FileLock, Settings, Layout as LayoutIcon, UserCheck,
} from "lucide-react";
import { CMS_SECTIONS } from "@/config/cmsSections";
import BackgroundEditor from "@/components/admin/BackgroundEditor";
import ImageCard from "@/components/admin/ImageCard";
import SectionPreviewRouter from "@/components/admin/SectionPreview";
import { invalidateContentCache } from "@/lib/useSiteContent";

// ─── Mapping section.id → Page parente ──────────────────────────────────
// Ordre dans le tableau = ordre d'affichage des onglets
const PAGES = [
    { id: "theme",     label: "Thème (couleurs)", icon: Settings, match: (id) => id === "theme" },
    { id: "emails",    label: "Emails",         icon: FileText,  match: (id) => id === "email-confirm" },
    { id: "home",      label: "Accueil",        icon: HomeIcon,  match: (id) => id.startsWith("home-v2") },
    { id: "pro",       label: "Professionnels", icon: Building,   match: (id) => /^(pro|optimisation|renego|multisites)-v2/.test(id) },
    { id: "part",      label: "Particuliers",   icon: Users,     match: (id) => /^(part|comparer|changer|demenagement|comprendre)-v2/.test(id) },
    { id: "suppliers", label: "Fournisseurs",   icon: BarChart3, match: (id) => id.startsWith("fournisseurs-v2") },
    { id: "veille",    label: "Veille Marché",  icon: FileText,  match: (id) => id.startsWith("veille-v2") },
    { id: "apropos",   label: "À propos",       icon: UserCheck, match: (id) => id.startsWith("apropos-v2") },
    { id: "header",    label: "Header",         icon: LayoutIcon,match: (id) => id === "header-v2" },
    { id: "footer",    label: "Footer",         icon: LayoutIcon,match: (id) => id === "footer" },
    { id: "common",    label: "Commun",         icon: Settings,  match: (id) => id === "common" },
    { id: "legacy",    label: "Autres pages",   icon: FileLock,  match: () => true },
];

function getPageForSection(section) {
    for (const p of PAGES) {
        if (p.match(section.id)) return p.id;
    }
    return "common"; // fallback
}

function _extractError(e) {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" · ");
    if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return e?.message || "Erreur inconnue";
}


/**
 * Structure du CMS : sections + champs.
 * - type: "text" | "longtext" | "image" | "number"
 * - default: valeur affichée si rien en DB (= valeur hardcodée actuelle dans la page)
 */


export default function AdminContent() {
    const [values, setValues] = useState({}); // key → value (DB)
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(new Set());
    const [activePage, setActivePage] = useState(() => {
        try { return localStorage.getItem("em_admin_content_page") || "home"; } catch { return "home"; }
    });
    const [openSection, setOpenSection] = useState(null);
    // Aperçu email de confirmation
    const [preview, setPreview] = useState(null); // { subject, html }
    const [previewLoading, setPreviewLoading] = useState("");

    async function openEmailPreview(type) {
        setPreviewLoading(type);
        try {
            const { data } = await api.post("/admin/email-confirm/preview", {
                type,
                subject: values[`email.confirm.${type}.subject`] || "",
                body: values[`email.confirm.${type}.body`] || "",
                delay: values[`email.confirm.${type}.delay`] || "",
                header: values["email.confirm.header"] || "",
                subtitle: values["email.confirm.subtitle"] || "",
                footer: values["email.confirm.footer"] || "",
                logo: values["email.confirm.logo"] || "",
            });
            setPreview(data);
        } catch (e) {
            toast.error("Aperçu : " + _extractError(e));
        } finally {
            setPreviewLoading("");
        }
    }

    // Persiste l'onglet sélectionné pour éviter de tout réinitialiser
    useEffect(() => {
        try { localStorage.setItem("em_admin_content_page", activePage); } catch {}
    }, [activePage]);

    // Sections groupées par page
    const sectionsByPage = useMemo(() => {
        const map = {};
        for (const p of PAGES) map[p.id] = [];
        for (const sec of CMS_SECTIONS) {
            if (sec.hidden) continue; // sections obsolètes (ancienne v1) masquées de l'éditeur
            const p = getPageForSection(sec);
            (map[p] || (map[p] = [])).push(sec);
        }
        return map;
    }, []);

    // Compte de champs modifiés (dirty) par page — pour le badge "(3)" sur l'onglet
    const dirtyByPage = useMemo(() => {
        const counts = {};
        for (const sec of CMS_SECTIONS) {
            const p = getPageForSection(sec);
            const n = sec.fields.filter((f) => dirty.has(f.key)).length;
            if (n > 0) counts[p] = (counts[p] || 0) + n;
        }
        return counts;
    }, [dirty]);

    const visibleSections = sectionsByPage[activePage] || [];

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content");
            const map = {};
            // 1. Pré-remplir chaque champ avec sa valeur par défaut (issue de cmsSections.js).
            //    Comme ça l'utilisateur peut juste retoucher un mot sans tout retaper.
            for (const section of CMS_SECTIONS) {
                for (const field of section.fields) {
                    if (field.default !== undefined && field.default !== "") {
                        map[field.key] = field.default;
                    }
                }
            }
            // 2. Écraser avec les valeurs réellement enregistrées en base si elles existent.
            for (const row of data) {
                if (row.value !== undefined && row.value !== null && row.value !== "") {
                    map[row.key] = row.value;
                }
            }
            setValues(map);
            setDirty(new Set());
        } catch (e) {
            toast.error("Chargement : " + _extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    function set(key, value) {
        setValues((v) => ({ ...v, [key]: value }));
        setDirty((d) => new Set(d).add(key));
    }

    async function saveAll() {
        if (dirty.size === 0) return;
        setSaving(true);
        try {
            const items = Array.from(dirty).map((key) => ({ key, value: values[key] ?? "" }));
            await api.put("/admin/content", { items });
            toast.success(`${items.length} champ${items.length > 1 ? "s" : ""} enregistré${items.length > 1 ? "s" : ""}`);
            setDirty(new Set());
            // Invalide le cache public pour que le site reflète immédiatement les modifs
            invalidateContentCache();
        } catch (e) {
            toast.error("Sauvegarde : " + _extractError(e));
        } finally {
            setSaving(false);
        }
    }

    async function uploadImage(key, file) {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(
                `/admin/content/upload?key=${encodeURIComponent(key)}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            // L'API persiste déjà le nouveau path → met juste à jour le state local
            setValues((v) => ({ ...v, [key]: data.url }));
            // L'upload est immédiatement persisté → on n'a pas besoin de le marquer dirty
            setDirty((d) => { const n = new Set(d); n.delete(key); return n; });
            invalidateContentCache();
            toast.success("Image téléversée");
        } catch (e) {
            toast.error("Upload : " + _extractError(e));
        }
    }

    if (loading) {
        return (
            <div className="em-container py-32 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…
            </div>
        );
    }

    return (
        <div className="em-container py-8">
            <p className="em-eyebrow">Réglages</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">
                Contenu du site
            </h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">
                Édite les textes, accroches et images affichés sur le site public, sans toucher au code.
                Les modifications sont publiées immédiatement après "Enregistrer".
            </p>

            <div className="mt-6 flex items-center justify-between gap-3 sticky top-24 z-30 bg-background/80 backdrop-blur py-3 -mx-2 px-2 rounded-2xl">
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border-2 border-border bg-card hover:border-primary/40"
                >
                    <RefreshCw className="h-4 w-4" /> Recharger
                </button>
                <button
                    onClick={saveAll}
                    disabled={saving || dirty.size === 0}
                    className="inline-flex items-center gap-2 rounded-full px-6 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {dirty.size > 0 ? `Enregistrer (${dirty.size})` : "Enregistré"}
                </button>
            </div>

            {/* ─── ONGLETS (colonne verticale à droite) + SECTIONS ─── */}
            <div className="mt-6 flex flex-col lg:flex-row-reverse gap-6">
                {/* Navigation verticale (à droite sur desktop, horizontale scrollable sur mobile) */}
                <nav className="lg:w-56 lg:shrink-0">
                    <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible p-1.5 bg-muted/50 rounded-2xl border border-border lg:sticky lg:top-6">
                        {PAGES.map((p) => {
                            const Icon = p.icon;
                            const count = sectionsByPage[p.id]?.length || 0;
                            if (count === 0) return null; // pas d'onglet vide
                            const dCount = dirtyByPage[p.id] || 0;
                            const active = activePage === p.id;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => { setActivePage(p.id); setOpenSection(null); }}
                                    className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors lg:w-full ${
                                        active
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-muted-foreground hover:bg-card hover:text-foreground"
                                    }`}
                                >
                                    <Icon className="h-4 w-4 shrink-0" />
                                    <span className="flex-1 text-left">{p.label}</span>
                                    <span className={`text-xs ${active ? "text-primary-foreground/70" : "text-muted-foreground/70"}`}>
                                        ({count})
                                    </span>
                                    {dCount > 0 && (
                                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                                            {dCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Sections de la page active */}
                <div className="flex-1 min-w-0 space-y-4">
                    {activePage === "emails" && (
                        <div className="rounded-2xl border border-border bg-card p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Eye className="h-4 w-4 text-primary" />
                                <span className="text-sm font-semibold">Aperçu du mail de confirmation</span>
                                <span className="text-xs text-muted-foreground">— avec tes valeurs en cours</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {[["devis", "Devis"], ["cee", "CEE & Solaire"], ["isolation", "Isolation/PAC"], ["contact", "Contact"]].map(([t, label]) => (
                                    <button
                                        key={t}
                                        onClick={() => openEmailPreview(t)}
                                        disabled={previewLoading === t}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50"
                                    >
                                        {previewLoading === t ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {visibleSections.length === 0 ? (
                        <div className="text-center text-muted-foreground py-12">
                            Aucune section éditable sur cette page.
                        </div>
                    ) : (
                        visibleSections.map((section) => (
                            <SectionBlock
                                key={section.id}
                                section={section}
                                open={openSection === section.id}
                                onToggle={() => setOpenSection(openSection === section.id ? null : section.id)}
                                values={values}
                                dirty={dirty}
                                onChange={set}
                                onUpload={uploadImage}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Modale d'aperçu email */}
            {preview && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setPreview(null)} />
                    <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border">
                            <div className="min-w-0">
                                <p className="text-xs text-muted-foreground">Objet</p>
                                <p className="font-semibold truncate">{preview.subject}</p>
                            </div>
                            <button onClick={() => setPreview(null)} className="rounded-lg p-2 hover:bg-secondary shrink-0"><X className="h-5 w-5" /></button>
                        </div>
                        <iframe
                            title="Aperçu email"
                            sandbox=""
                            srcDoc={preview.html}
                            className="w-full flex-1 min-h-[60vh] bg-white"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}


function SectionBlock({ section, open, onToggle, values, dirty, onChange, onUpload }) {
    const dirtyCount = section.fields.filter((f) => dirty.has(f.key)).length;
    return (
        <div className="rounded-3xl border border-border bg-card overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-3 p-5 hover:bg-muted/30 transition-colors"
            >
                <div className="flex items-center gap-3 text-left">
                    <ChevronRight className={`h-5 w-5 transition-transform ${open ? "rotate-90" : ""}`} />
                    <div>
                        <h2 className="font-display text-xl font-bold text-foreground">{section.title}</h2>
                        {section.description && (
                            <p className="text-sm text-muted-foreground mt-0.5">{section.description}</p>
                        )}
                    </div>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                    {section.fields.length} champs{dirtyCount > 0 && <strong className="text-primary"> · {dirtyCount} modifiés</strong>}
                </span>
            </button>

            {open && (
                <div className="border-t border-border bg-muted/10">
                    {/* Aperçu live (si la section en a un défini) */}
                    <SectionPreviewWrapper sectionId={section.id} values={values} />

                    {/* Grille de champs */}
                    <div className="p-6 space-y-5">
                        {section.fields.map((field) => (
                            <FieldEditor
                                key={field.key}
                                field={field}
                                value={values[field.key] ?? ""}
                                isDirty={dirty.has(field.key)}
                                onChange={(v) => onChange(field.key, v)}
                                onUpload={(file) => onUpload(field.key, file)}
                                allValues={values}
                                setKey={onChange}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


/** Wrapper qui n'affiche le bloc d'aperçu que si le router en fournit un. */
function SectionPreviewWrapper({ sectionId, values }) {
    // SectionPreviewRouter retourne null si pas de preview défini pour cette section.
    // On l'appelle comme fonction (ce n'est qu'un switch) pour pouvoir conditionner le wrapper.
    const node = SectionPreviewRouter({ sectionId, values });
    if (!node) return null;
    return (
        <div className="border-b border-border p-6 bg-background">
            {node}
        </div>
    );
}


function FieldEditor({ field, value, isDirty, onChange, onUpload, allValues, setKey }) {
    const ringClass = isDirty ? "ring-2 ring-primary/40" : "";
    const isModified = value !== undefined && value !== "" && value !== field.default;
    return (
        <div className={`rounded-xl bg-background p-4 ${ringClass}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5" />
                    {field.label}
                </label>
                {isModified && (
                    <button
                        type="button"
                        onClick={() => onChange(field.default ?? "")}
                        className="text-xs text-muted-foreground hover:text-primary underline"
                        title="Repasser au texte par défaut"
                    >
                        Réinitialiser
                    </button>
                )}
            </div>
            {field.type === "background" ? (
                <BackgroundEditor
                    src={value}
                    opacity={parseFloat(allValues[`${field.key}.opacity`] ?? "100")}
                    blur={parseFloat(allValues[`${field.key}.blur`] ?? "0")}
                    overlayColor={allValues[`${field.key}.overlay_color`] || "#0A3B2A"}
                    overlayOpacity={parseFloat(allValues[`${field.key}.overlay_opacity`] ?? "0")}
                    fixed={allValues[`${field.key}.fixed`] === "true" || allValues[`${field.key}.fixed`] === true}
                    resolveAssetUrl={assetUrl}
                    onChange={({ src, opacity, blur, overlayColor, overlayOpacity, fixed }) => {
                        if (src !== value) setKey(field.key, src);
                        setKey(`${field.key}.opacity`, String(Math.round(opacity)));
                        setKey(`${field.key}.blur`, String(blur.toFixed(1)));
                        setKey(`${field.key}.overlay_color`, overlayColor);
                        setKey(`${field.key}.overlay_opacity`, String(Math.round(overlayOpacity)));
                        setKey(`${field.key}.fixed`, fixed ? "true" : "false");
                    }}
                    onUpload={onUpload}
                />
            ) : field.type === "image" ? (
                <ImageCard
                    value={value}
                    fallback={field.default}
                    label={field.label}
                    aspect="16/9"
                    onChange={onChange}
                    onUpload={onUpload}
                    focal={{
                        focal_x: parseFloat(allValues[`${field.key}.focal_x`] ?? "50"),
                        focal_y: parseFloat(allValues[`${field.key}.focal_y`] ?? "50"),
                        zoom: parseFloat(allValues[`${field.key}.zoom`] ?? "1"),
                    }}
                    onFocalChange={({ focal_x, focal_y, zoom }) => {
                        setKey(`${field.key}.focal_x`, String(focal_x.toFixed(1)));
                        setKey(`${field.key}.focal_y`, String(focal_y.toFixed(1)));
                        setKey(`${field.key}.zoom`, String(zoom.toFixed(2)));
                    }}
                />
            ) : field.type === "longtext" ? (
                <textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={field.default}
                    rows={3}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
            ) : field.type === "color" ? (
                <div className="flex items-center gap-3">
                    <input
                        type="color"
                        value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : (field.default || "#000000")}
                        onChange={(e) => onChange(e.target.value)}
                        className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-border bg-background p-1"
                        title="Choisir une couleur"
                    />
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.default}
                        className="w-40 rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                    />
                    <span className="h-8 flex-1 rounded-lg border border-border" style={{ background: value || field.default }} />
                </div>
            ) : (
                <input
                    type={field.type === "number" ? "number" : "text"}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={field.default}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
            )}
            <p className="text-xs text-muted-foreground/60 mt-2 font-mono">{field.key}</p>
        </div>
    );
}
